import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { assessmentService } from '../../services/api'
import { AssessmentSummary, getLevelColor, formatDuration } from '../../types/assessment'

// Mock skills data - in production this would come from API
const AVAILABLE_SKILLS = [
  { id: 'skill-1', name: 'JavaScript', category: 'Development', description: 'Core JavaScript concepts, ES6+, async/await' },
  { id: 'skill-2', name: 'React', category: 'Development', description: 'React hooks, state management, component patterns' },
  { id: 'skill-3', name: 'TypeScript', category: 'Development', description: 'Type system, generics, advanced patterns' },
  { id: 'skill-4', name: 'Node.js', category: 'Development', description: 'Server-side JavaScript, APIs, middleware' },
  { id: 'skill-5', name: 'Python', category: 'Development', description: 'Python fundamentals, data structures, OOP' },
  { id: 'skill-6', name: 'AWS', category: 'Cloud', description: 'EC2, S3, Lambda, IAM, networking' },
  { id: 'skill-7', name: 'Docker', category: 'DevOps', description: 'Containers, images, compose, best practices' },
  { id: 'skill-8', name: 'Kubernetes', category: 'DevOps', description: 'Deployments, services, ingress, helm' },
  { id: 'skill-9', name: 'SQL', category: 'Data', description: 'Queries, joins, optimization, database design' },
  { id: 'skill-10', name: 'Security', category: 'Security', description: 'OWASP, authentication, encryption, secure coding' },
]

export function AssessmentsListPage() {
  const navigate = useNavigate()
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'skills' | 'history'>('skills')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  useEffect(() => {
    loadAssessments()
  }, [])

  const loadAssessments = async () => {
    try {
      const response = await assessmentService.list()
      setAssessments(response.data.assessments || [])
    } catch (err) {
      console.error('Failed to load assessments:', err)
    } finally {
      setLoading(false)
    }
  }

  const startAssessment = (skillId: string) => {
    navigate(`/expert/assessments/new?skill=${skillId}`)
  }

  const resumeAssessment = (assessmentId: string) => {
    navigate(`/expert/assessments/${assessmentId}`)
  }

  // Get unique categories
  const categories = ['all', ...new Set(AVAILABLE_SKILLS.map((s) => s.category))]

  // Filter skills by category
  const filteredSkills =
    selectedCategory === 'all'
      ? AVAILABLE_SKILLS
      : AVAILABLE_SKILLS.filter((s) => s.category === selectedCategory)

  // Check for in-progress assessment for a skill
  const getInProgressAssessment = (skillId: string) =>
    assessments.find((a) => a.skill_id === skillId && a.status === 'in_progress')

  // Get completed assessment for a skill (most recent)
  const getCompletedAssessment = (skillId: string) =>
    assessments.find((a) => a.skill_id === skillId && a.status === 'completed')

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Skill Assessments</h1>
          <p className="mt-2 text-gray-600">
            Verify your skills with our adaptive assessments. Earn badges that showcase your
            expertise to potential employers.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('skills')}
            className={`px-4 py-3 font-medium text-sm border-b-2 -mb-px transition-colors ${
              activeTab === 'skills'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Available Skills
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 font-medium text-sm border-b-2 -mb-px transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Assessment History
          </button>
        </div>

        {activeTab === 'skills' && (
          <>
            {/* Category filter */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border'
                  }`}
                >
                  {category === 'all' ? 'All Skills' : category}
                </button>
              ))}
            </div>

            {/* Skills grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSkills.map((skill) => {
                const inProgress = getInProgressAssessment(skill.id)
                const completed = getCompletedAssessment(skill.id)

                return (
                  <div
                    key={skill.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{skill.name}</h3>
                        <span className="text-xs text-gray-500">{skill.category}</span>
                      </div>
                      {completed?.final_level && (
                        <span
                          className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getLevelColor(
                            completed.final_level
                          )}`}
                        >
                          {completed.final_level}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-4">{skill.description}</p>

                    {inProgress ? (
                      <button
                        onClick={() => resumeAssessment(inProgress.id)}
                        className="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
                      >
                        Resume Assessment
                      </button>
                    ) : (
                      <button
                        onClick={() => startAssessment(skill.id)}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        {completed ? 'Retake Assessment' : 'Start Assessment'}
                      </button>
                    )}

                    {completed && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Last taken:{' '}
                        {new Date(completed.completed_at!).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-2 text-gray-500">Loading history...</p>
              </div>
            ) : assessments.length === 0 ? (
              <div className="p-8 text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900">No assessments yet</h3>
                <p className="text-gray-500 mt-1">
                  Start your first assessment to see your history here.
                </p>
                <button
                  onClick={() => setActiveTab('skills')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Browse Skills
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {assessments.map((assessment) => (
                  <div
                    key={assessment.id}
                    className="p-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-gray-900">
                          {assessment.skill_name || 'Skill Assessment'}
                        </h4>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            assessment.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : assessment.status === 'in_progress'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {assessment.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span>
                          {new Date(assessment.started_at).toLocaleDateString()}
                        </span>
                        <span>{assessment.questions_answered} questions</span>
                        {assessment.final_level && (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium text-white rounded-full ${getLevelColor(
                              assessment.final_level
                            )}`}
                          >
                            {assessment.final_level}
                          </span>
                        )}
                        {assessment.percentile && (
                          <span>Top {100 - assessment.percentile}%</span>
                        )}
                      </div>
                    </div>
                    {assessment.status === 'in_progress' && (
                      <button
                        onClick={() => resumeAssessment(assessment.id)}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600"
                      >
                        Resume
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
